/**
 * The Studio's document, and the arithmetic it shares with the edge — STUDIO_DESIGN.md 3.1,
 * 3.2, 3.3.
 *
 * PURE. This module imports types from `./wire`, a string helper from `./canonical` and
 * numbers from `./limits`, and nothing else: no DOM, no services, no React, no storage. It
 * is what makes `plan()` testable against the Kotlin fixture with no environment at all.
 *
 * TWO SHAPES, ON PURPOSE. `StudioCompositionV1` (generated) is the WIRE shape, and the
 * generator renders its array bounds as tuple types -- `tracks: [Track] | [Track, Track]`,
 * `VisualTrack.clips: [PhotoClip | VideoClip, ...(PhotoClip | VideoClip)[]]`. Those are
 * exactly right for a payload and unusable for an editor, where a clip list is spliced,
 * reordered and emptied. So the editor's `StudioDocument` is flat, and `toWire`/`fromWire`
 * are the only places the two meet.
 *
 * WHAT IS NOT HERE. The semantic rules that produce refusals live in `./check` beside the
 * closed code set they draw from (P5a.3). This module owns only the arithmetic those rules
 * consume, plus the two places where the client acts on a rule rather than reporting it:
 * `clampTransitionMs` (the transition is clamped, never refused -- Phase 2 carry-forward)
 * and `nextRevision` (the idempotency contract). The FRAME grid -- ironman's
 * `processors/compose/FilterGraphBuilder.kt` `FrameGrid` (:76-116), where a millisecond
 * becomes a frame index and a transition is snapped to whole frames and truncated to whole
 * microseconds -- is `./frame-grid.ts` (P5a.4). Everything below is the millisecond
 * identity only, which is the layer `CompositionPlan.of` itself works in.
 */
import { canonicalJson } from './canonical';
import type {
    AudioClip, AudioTrack, ClipAudio, Crop, Fit, Frame, PhotoClip,
    StudioCompositionV1, Track, Transition, VideoClip, VisualTrack,
} from './wire';

export type {
    AudioClip, AudioTrack, ClipAudio, Crop, Fit, Frame, PhotoClip,
    StudioCompositionV1, Track, Transition, VideoClip, VisualTrack,
};

/** A clip on the visual track. The audio track carries `AudioClip` and only `AudioClip`. */
export type VisualClip = PhotoClip | VideoClip;

/**
 * The song as the EDITOR holds it -- the wire's `AudioClip` plus one editor-only fact (round 6, S4).
 *
 * `followsVideo` is true while the song's end is the editor's own doing: trimmed to the video at
 * placement, or by the panel's "Fit to video". While it is true, `normalised()` in the owner
 * re-clamps `trimEndMs` to the video's length on EVERY commit -- a hold that grows from 4 to 6 s
 * grows the song with it, and a hold that shrinks shrinks it (H6 QA#7: two seconds of digital
 * silence; SDE#13: the lane said "Trimmed to the length of your video" about a 32 s song over a
 * 20.5 s video). The flag turns false the moment the user trims by hand (a lane handle, a panel
 * field), and from then on the trim is theirs. `musicToWire` copies named fields only, so the
 * flag never reaches the server; `fromWire` leaves it undefined, which reads as false.
 */
export type MusicClip = AudioClip & {
    followsVideo?: boolean;
    /**
     * S2-02 (review 7): the song plays to the END OF ITS FILE -- it follows a video that is at least as long as the
     * song, so nothing cuts it. `trimEndMs` then holds the probed length (what the panel and the lane show) and
     * `musicToWire` OMITS the end, so the server reads to EOF. Before, the end was the probe less
     * `MUSIC_LENGTH_TOLERANCE_MS` -- a 6.0 s song said "0:06 long" and was cut at 5.74 s on every export. The
     * tolerance exists for an end the USER chose near the file's end (MP3 lengths are decoder-dependent); an
     * untouched song has no chosen end to protect. Editor-only, like `followsVideo`: never on the wire.
     */
    toEnd?: boolean;
};

// --------------------------------------------------------------------- constants

export const COMPOSITION_VERSION = 1;

/** Identifies the producer in every submission (schema: `createdWith`, 1..64 chars). */
export const CREATED_WITH = 'studio/1';

/** The schema's `holdMs` minimum, and the shortest clip the UI will let a user make. */
export const MIN_CLIP_MS = 500;

/** The schema's `holdMs` maximum. `STUDIO_LIMITS.maxHoldMs` may lower it, never raise it. */
export const MAX_HOLD_MS = 60_000;

/** CompositionValidator.MAX_TRANSITION_MS (`CompositionValidator.kt:25`). */
export const MAX_TRANSITION_MS = 2_000;

/**
 * CompositionValidator.MIN_INTERIOR_MS — the slice of the shortest clip a transition may
 * never eat, because an interior clip is cut at BOTH ends and has to survive 1.5 frames of
 * rounding at 24 fps (`CompositionValidator.kt:40-68`).
 *
 * Mirror the FORMULA, never the number (the Kotlin KDoc says so at :64-66, and the Phase 2
 * ledger repeats it). Every derived quantity in this file is computed from this constant,
 * so moving it moves the clamp, the minimum clip length and the UI's slider bound together.
 */
export const MIN_INTERIOR_MS = 63;

/** The schema's arithmetic bound on every millisecond field: 24 h. */
export const MAX_TIMELINE_MS = 86_400_000;

/** `IdempotentScheduleKey.MAX_PROJECT_REVISION` — a higher revision throws at schedule time. */
export const MAX_REVISION = 1_000_000;

/** CompositionValidator.CROP_ASPECT_TOLERANCE (`:71`) — 1 % of the frame's aspect. */
export const CROP_ASPECT_TOLERANCE = 0.01;

/** The schema's `Crop.w`/`Crop.h` minimum. */
export const MIN_CROP_FRACTION = 0.05;

const INT_MAX = 2_147_483_647;
const INT_MIN = -2_147_483_648;

// --------------------------------------------------------------------- the document

/**
 * What the editor holds, what history snapshots and what persistence stores.
 *
 * `transition.durationMs` is always a number here (never `undefined`, which the wire type
 * allows) so a slider always has a value to show; `toWire` drops it again when it is 0.
 *
 * **A clip's `trimEndMs` is always a number here too**, for the same reason and with the same
 * shape: the wire type allows `undefined` since the schema stopped requiring the key, but every
 * handle, readout and clamp in the editor needs a concrete end to draw and to drag. The editor
 * seeds it at the source's measured length and re-clamps it there on every commit, so "the user
 * never trimmed this" is expressed as "the end EQUALS the source's length", not as an absence.
 * `toWire` is the one place that turns that into an absence, and nothing reads a wire clip's
 * `trimEndMs` except through `effectiveTrimEndMs`. The PERSISTED format is unchanged: a project
 * stored before this change reloads with the same numbers and behaves identically.
 */
export interface StudioDocument {
    projectId: string;
    revision: number;
    frame: Frame;
    transition: Transition & { durationMs: number };
    clips: VisualClip[];
    music: MusicClip | null;
}

/** What ironman knows about an input and the browser may or may not. Mirrors `ProbeInfo`. */
export interface ProbeInfo {
    durationMs?: number | null;
    width?: number | null;
    height?: number | null;
    hasAudio?: boolean;
    /**
     * **This input is an ANIMATED IMAGE** (APNG, animated WebP, GIF) — the one field here the
     * Kotlin `ProbeInfo` has no twin for, because it is not a fact about the probe, it is a fact
     * about what a probe can answer.
     *
     * The browser learns it from the bytes (`media/animated.ts`'s `sniffAnimated`) before the clip
     * is placed, and `toWire` reads it for exactly one decision: whether an end may be OMITTED.
     * `ffprobe` on an APNG reports `duration=N/A` and `nb_frames=N/A` — measured on the owner's own
     * `elephant.png` and reproduced locally — so "absent means to the end of the source" has no
     * answer on the far side and the edge resolves the end to 0 (export failure
     * `9vbjspipq2gw`: `clip 2 … duration=0ms`). An animated image therefore always STATES its end.
     *
     * Absent/false means "an ordinary video container the edge can probe", which is the behaviour
     * every caller had before this field existed.
     */
    animated?: boolean;
}

/** Keyed by the clip's `input` index, exactly as the Kotlin `Map<Int, ProbeInfo>` is. */
export type ProbeMap = Readonly<Record<number, ProbeInfo | undefined>>;

/** The field set is fixed by STUDIO_PLAN.md; do not add to it. */
export interface PlannedClip {
    index: number;
    input: number;
    startMs: number;
    durationMs: number;
    hasAudio: boolean;
}

export interface CompositionPlan {
    clips: PlannedClip[];
    totalMs: number;
    transitionMs: number;
}

// --------------------------------------------------------------------- accessors

export function visualTrackOf(c: StudioCompositionV1): VisualTrack {
    const found = (c.tracks as Track[]).find((t) => t.kind === 'VISUAL');
    if (!found) {
        // The schema's `minContains`/`maxContains` make this unreachable for anything that
        // passed validation; it is an error rather than a silent empty track because a
        // silently empty timeline is how a user loses their project without being told.
        throw new Error('a v1 composition has exactly one VISUAL track');
    }
    return found as VisualTrack;
}

export function audioTrackOf(c: StudioCompositionV1): AudioTrack | null {
    return ((c.tracks as Track[]).find((t) => t.kind === 'AUDIO') as AudioTrack) ?? null;
}

export function visualClipsOf(c: StudioCompositionV1): VisualClip[] {
    return visualTrackOf(c).clips as VisualClip[];
}

export function musicClipOf(c: StudioCompositionV1): AudioClip | null {
    const track = audioTrackOf(c);
    return track && track.clips.length > 0 ? (track.clips[0] as AudioClip) : null;
}

/**
 * A VIDEO clip with no `audio` key keeps its source audio (STUDIO_DESIGN.md 3.1). The
 * absence IS the value, stated in the schema's `default`, in the Kotlin binder and in
 * `VideoClip.audio`'s constructor default (`Composition.kt:179-187`) -- and the golden
 * fixture's clip 2 depends on it.
 */
export function clipAudioOf(clip: VideoClip): { mode: 'KEEP' | 'MUTE'; volume: number } {
    return { mode: clip.audio?.mode ?? 'KEEP', volume: clip.audio?.volume ?? 1 };
}

/**
 * `Transition.effectiveDurationMs` (`Composition.kt:130`): a NONE boundary costs no time
 * however long it claims.
 */
export function effectiveDurationMs(t: Transition | null | undefined): number {
    if (!t || t.type === 'NONE') return 0;
    return t.durationMs ?? 0;
}

/** The boundary entering clip `index`: its own override, else the default. Clip 0 has none. */
export function effectiveTransitionAt(c: StudioCompositionV1, index: number): Transition {
    if (index <= 0) return { type: 'NONE', durationMs: 0 };
    return visualClipsOf(c)[index]?.transitionIn ?? c.transition;
}

/**
 * **THE resolver for an absent `trimEndMs`** — the one place this module turns "no end" into a
 * number, and the browser half of `Clip.effectiveTrimEndMs` in the shared library
 * (`Composition.kt`). Nothing may read `trimEndMs` off a WIRE clip except through here.
 *
 * `trimEndMs` is optional on the wire (`composition-v1.schema.json`) and absent means "to the end
 * of the source". It is absent for a reason no rounding rule could fix: a client cannot name a
 * file's end. The owner's measurement of the QA file `016gkqNT9hr-…mp4` — 397 frames at 30 fps,
 * LAST FRAME PTS 13.200000 s, container `format.duration` 13.233333 s, audio 13.230658 s, and the
 * probe we store, 13 210.703125 ms — is three different ends, and the one we store is a STREAM
 * duration ~23 ms short of where the file stops.
 *
 * @param sourceDurationMs what the browser measured for THIS clip's source, or null/undefined when
 *   it has not measured one. The result is then `undefined`, meaning "unknown" — never 0, which
 *   would make an untrimmed clip look empty.
 *
 * **The DOCUMENT is not the wire.** `StudioDocument`'s clips always carry a real `trimEndMs`
 * (see the interface), exactly as they always carry a real `transition.durationMs`, so the editor
 * and its views keep reading the field directly. Absence is created by `toWire` and lives only
 * downstream of it.
 */
export function effectiveTrimEndMs(
    clip: VideoClip | AudioClip,
    sourceDurationMs?: number | null,
): number | undefined {
    if (clip.trimEndMs != null) return clip.trimEndMs;
    return sourceDurationMs != null ? sourceDurationMs : undefined;
}

/**
 * Time a clip occupies on its track BEFORE any transition overlap.
 *
 * `sourceDurationMs` is only consulted for a clip whose `trimEndMs` is absent — a wire clip, never
 * a document one. With neither, the length is unknown and this returns 0 so the arithmetic stays
 * finite; the caller that cares (`check.ts`) tests the resolved end itself rather than the length.
 */
export function clipDurationMs(clip: VisualClip | AudioClip, sourceDurationMs?: number | null): number {
    if (clip.kind === 'PHOTO') return clip.holdMs;
    const end = effectiveTrimEndMs(clip, sourceDurationMs);
    if (end == null) return 0;
    return end - (clip.trimStartMs ?? 0);
}

/**
 * A MEASURED source length turned into the length that source will actually occupy — the one
 * conversion between "how long is this file" and "how long will this clip be".
 *
 * Review6 BUG-22: a 0.36 s animated GIF was placed at the 4.0 s photo default, the export sheet
 * quoted 0:08.0, and the composition retimed itself to 0:04.5 *after* the user had committed —
 * a number promised and then silently withdrawn. The ordering fix is to resolve the source's
 * real length first (`media/animated.ts`'s `sniffAnimated` answers it from the bytes, before the
 * clip is given a hold), and this is the second half of it: a measurement is not yet a clip
 * length.
 *
 * **THE LENGTH IS THE BYTES' (R14-V7, round 14).** The first version of this lifted a source under
 * `MIN_CLIP_MS` to the floor, on the claim that 500 ms is "what both server tiers render". It is
 * not, and the two rules could not both hold for any animation under half a second: the server's
 * `CompositionValidator` refuses a PRESENT `trimEndMs` past `ProbeInfo.durationMs` (one frame of
 * tolerance), `CompositionPlan` resolves an ABSENT end to that same probe, and `FilterGraphBuilder`
 * emits no `-t` at all for it — ffmpeg reads the file to EOF and nothing pads it. So a 200 ms GIF
 * placed at 500 ms was refused by the editor's own pre-flight ("Clip 1 is trimmed past the end of
 * its video.", `check.ts`, the twin of the Kotlin rule) and "Fix it" had nothing legal to move
 * to. A clip is never longer than its source; `MIN_CLIP_MS` is the floor on what a USER may trim
 * a longer source down to (`clampTrimToSource`), not a length the editor invents.
 *
 * Floored, never rounded (QA 7b59d2a8): the number lands on a trim bound, the validator compares
 * that bound against the probe on the other tier, and a bound rounded UP is `trim_out_of_range`.
 * Never under 1 ms: a measurement that floors to nothing is not a clip.
 *
 * @param measuredMs what was measured, or null/undefined when nothing was. `null` back means
 *   "still unknown" — the caller must keep waiting rather than quote anything, which is the
 *   state it already handles. 0, negative, NaN and Infinity are not measurements either.
 */
export function placedClipLengthMs(measuredMs: number | null | undefined): number | null {
    if (measuredMs == null || !Number.isFinite(measuredMs) || measuredMs <= 0) return null;
    return Math.max(1, Math.floor(measuredMs));
}

/**
 * The shortest a clip of a `sourceDurationMs`-long source may be: `MIN_CLIP_MS`, or the whole
 * source when the source itself is shorter (R14-V7). Every floor in the editor that says
 * "never under MIN_CLIP_MS" means this, because a floor above the source is a trim past its end.
 */
export function clipFloorMs(sourceDurationMs: number | null | undefined): number {
    if (sourceDurationMs == null || !Number.isFinite(sourceDurationMs) || sourceDurationMs <= 0) return MIN_CLIP_MS;
    return Math.min(MIN_CLIP_MS, Math.max(1, Math.floor(sourceDurationMs)));
}

/**
 * THE repair for a trim that reaches past its source (R14-V7): the end comes down to the source's
 * floored length, and the start comes down with it only as far as it must to keep `clipFloorMs`
 * of clip — never up, since raising the start is what empties a clip. The SAME object comes back
 * when nothing was past the end, so a caller can tell a repair from a no-op by reference.
 *
 * `sourceDurationMs` unknown → the clip is returned as it is: there is nothing to clamp against,
 * and the pre-flight does not fire for an unmeasured source either.
 */
export function clampTrimToSource(clip: VideoClip, sourceDurationMs: number | null | undefined): VideoClip {
    if (sourceDurationMs == null || !Number.isFinite(sourceDurationMs) || sourceDurationMs <= 0) return clip;
    // `max(1, …)` as `placedClipLengthMs` / `clipFloorMs` (SR2 #8): a 0.2 ms source is a 1 ms clip, never an empty one.
    const ceilingMs = Math.max(1, Math.floor(sourceDurationMs));
    if (clip.trimEndMs <= ceilingMs) return clip;
    const floor = clipFloorMs(ceilingMs);
    const trimStartMs = Math.min(clip.trimStartMs, Math.max(0, ceilingMs - floor));
    return { ...clip, trimStartMs, trimEndMs: ceilingMs };
}

// --------------------------------------------------------------------- the plan

/**
 * Clamp into the Int range instead of wrapping, because the Kotlin side does
 * (`CompositionPlan.kt:100-105`) and `plan()` has to produce an EQUAL object. A JS number is
 * a double, so nothing here can overflow on its own -- this exists purely so the two sides
 * answer the same thing for a document built to overflow the other one.
 */
function saturatingInt(value: number): number {
    if (value > INT_MAX) return INT_MAX;
    if (value < INT_MIN) return INT_MIN;
    return value;
}

function hasAudioOf(clip: VisualClip, probes?: ProbeMap | null): boolean {
    if (clip.kind === 'PHOTO') return false;
    const probed = probes ? probes[clip.input] : undefined;
    return clipAudioOf(clip).mode === 'KEEP' && (probed?.hasAudio ?? true);
}

/**
 * THE timing identity: `start_i = Σ_{j<i} d_j − Σ_{k≤i} T_k`.
 *
 * A transcription of `CompositionPlan.of` (springboot-shared-library
 * `compose/CompositionPlan.kt:72-124`), not an interpretation of it: the overlap for a
 * boundary is accumulated BEFORE the clip it enters is pushed, every boundary contributes
 * its OWN effective duration (an override, else the default), `transitionMs` reports the
 * top-level default rather than any flattened per-boundary value (`:89`, KDoc `:26-29`),
 * and both accumulators are saturated at the `Int` boundary (`:100-105`).
 *
 * Equal, field for field, to the Kotlin plan on the golden fixture — which is the contract
 * STUDIO_PLAN.md:130 fixes and `composition.golden.spec.ts` asserts against the shared
 * library's own fixture file. Everything the preview draws and everything the ruler measures
 * comes from here, so that the picture in the browser and the file the edge produces are the
 * same video.
 */
export function plan(c: StudioCompositionV1, probes?: ProbeMap | null): CompositionPlan {
    const clips = visualClipsOf(c);
    const planned: PlannedClip[] = [];
    let elapsed = 0;
    let overlap = 0;
    clips.forEach((clip, index) => {
        overlap += effectiveDurationMs(effectiveTransitionAt(c, index));
        // Each clip resolves against ITS OWN input's probe — an absent `trimEndMs` here is a wire
        // clip that means "to the end of the source".
        const durationMs = clipDurationMs(clip, probes?.[clip.input]?.durationMs);
        planned.push({
            index,
            input: clip.input,
            startMs: saturatingInt(elapsed - overlap),
            durationMs,
            hasAudio: hasAudioOf(clip, probes),
        });
        elapsed += durationMs;
    });
    return {
        clips: planned,
        totalMs: saturatingInt(elapsed - overlap),
        transitionMs: effectiveDurationMs(c.transition),
    };
}

/** `plan()` for the editor's own shape. */
export function planOf(doc: StudioDocument, probes?: ProbeMap | null): CompositionPlan {
    // The same probes go into the projection and the plan: the wire `plan()` reads must be the
    // wire that would be SENT, or a clip omitted from one and present in the other would plan to a
    // different length than it renders at.
    return plan(toWire(doc, probes), probes);
}

// --------------------------------------------------------------------- the clamp

/**
 * The shortest clip's length, or 0 when no clip's length is KNOWN.
 *
 * Only clips with a resolvable length are counted (`CompositionValidator.kt` does the same). A wire
 * clip whose `trimEndMs` is absent and whose source was never measured has no length, and counting
 * that as 0 would drop the transition ceiling to 0 and mark every transition in a valid project
 * `transition_too_long` — a false refusal far worse than the rule it is enforcing.
 */
export function shortestClipMs(clips: readonly VisualClip[], probes?: ProbeMap | null): number {
    const known = clips
        .map(c => (c.kind === 'PHOTO' || effectiveTrimEndMs(c, probes?.[c.input]?.durationMs) != null
            ? clipDurationMs(c, probes?.[c.input]?.durationMs)
            : null))
        .filter((d): d is number => d != null);
    if (known.length === 0) return 0;
    return known.reduce((min, d) => Math.min(min, d), Number.POSITIVE_INFINITY);
}

/**
 * `min(2000, (shortest − MIN_INTERIOR_MS) / 2)`, floored at 0 — `CompositionValidator.kt:125`.
 *
 * `Math.trunc`, not `Math.floor`: Kotlin's `/` on Ints truncates toward zero, and the two
 * differ for a negative numerator (Kotlin `(-7)/2 == -3`, `Math.floor(-3.5) === -4`). The
 * `Math.max(0, …)` hides the difference in every reachable case, and using `trunc` means
 * the two expressions are the same function rather than the same function where it matters.
 */
export function transitionCeilingMs(shortestMs: number): number {
    return Math.max(0, Math.min(MAX_TRANSITION_MS, Math.trunc((shortestMs - MIN_INTERIOR_MS) / 2)));
}

/**
 * The client CLAMPS rather than refusing (Phase 2 carry-forward, STUDIO_PLAN_P2 ledger).
 *
 * A user dragging a clip shorter must not be told "your transition is now illegal" -- the
 * transition simply gets shorter with it, and the project stays exportable at every frame
 * of the drag. The refusal still exists on both server tiers; the editor's job is never to
 * reach it.
 */
export function clampTransitionMs(requestedMs: number, shortestMs: number): number {
    return Math.max(0, Math.min(Math.trunc(requestedMs), transitionCeilingMs(shortestMs)));
}

/**
 * The exact integer inverse of the ceiling: the shortest a clip may be for `transitionMs`
 * to survive. `ceiling(d) ≥ T  ⟺  trunc((d−63)/2) ≥ T  ⟺  d ≥ 2T + 63`.
 *
 * For the default 500 ms crossfade that is 1063 ms. The number is derived, never typed.
 */
export function minClipMsFor(transitionMs: number): number {
    return Math.max(MIN_CLIP_MS, 2 * Math.max(0, Math.trunc(transitionMs)) + MIN_INTERIOR_MS);
}

/**
 * Clamp the default boundary and every per-clip override against the current shortest clip.
 *
 * Returns the SAME object when nothing changed, so `history.commit`'s reference check can
 * tell a real edit from a no-op re-render without a deep compare.
 *
 * A document with fewer than two clips is returned untouched, and that is a CORRECTNESS rule
 * rather than a shortcut (P5a.2 review, I1): there is no boundary to clamp, and the clamp only
 * ever lowers. On an empty timeline `shortestClipMs` is 0, so the ceiling is 0 and the user's
 * 500 ms crossfade would be rewritten to 0 the moment the editor opened -- and never restored,
 * because nothing here raises it again when the first clips arrive. The same holds for one
 * clip: a single 600 ms photo would quietly cut the default to 268 ms for the clip that has
 * not been added yet.
 */
export function withClampedTransition(doc: StudioDocument): StudioDocument {
    if (doc.clips.length < 2) return doc;
    const shortest = shortestClipMs(doc.clips);
    const ceiling = transitionCeilingMs(shortest);
    const topWanted = effectiveDurationMs(doc.transition);
    const topClamped = Math.min(topWanted, ceiling);
    let changed = topClamped !== doc.transition.durationMs && doc.transition.type !== 'NONE';
    const clips = doc.clips.map((clip) => {
        const t = clip.transitionIn;
        if (!t || t.type === 'NONE') return clip;
        const wanted = t.durationMs ?? 0;
        const clamped = Math.min(wanted, ceiling);
        if (clamped === wanted) return clip;
        changed = true;
        return { ...clip, transitionIn: { ...t, durationMs: clamped } };
    });
    if (!changed) return doc;
    return {
        ...doc,
        transition: doc.transition.type === 'NONE'
            ? doc.transition
            : { ...doc.transition, durationMs: topClamped },
        clips,
    };
}

// --------------------------------------------------------------------- wire conversion

/**
 * Six decimals, everywhere a fraction reaches the wire or the editor's own crop arithmetic.
 *
 * ONE definition (whole-branch review, M10): `frame.ts` had the same expression, and two
 * roundings that could drift by a digit would show up as a crop whose aspect the server
 * measures differently from the client — a refusal the user cannot act on. Six is the
 * resolution the schema's crop fractions are written at, and it is far inside
 * `CROP_ASPECT_TOLERANCE` (1 %), so rounding here can never move a legal crop out of range.
 */
export const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/** Drop every key whose value is `undefined`, so the wire object has no empty slots. */
function compact<T extends object>(o: T): T {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(o)) {
        const value = (o as Record<string, unknown>)[key];
        if (value !== undefined) out[key] = value;
    }
    return out as T;
}

function cropToWire(c: Crop): Crop {
    return { x: round6(c.x), y: round6(c.y), w: round6(c.w), h: round6(c.h) };
}

/**
 * A clip's crop on the wire: ONLY under effective fit COVER (round 4, S3; `model/crops.ts`).
 *
 * Both CONTAIN fits show the whole picture, so a crop on such a clip changes nothing the user
 * can see -- and is exactly what ironman refused when it had gone stale ("The crop on clip 1
 * does not match the video shape"). What the editor cannot see, the server must not be asked
 * to judge. A crop of the whole picture is no crop and is not sent either.
 */
function cropOnWire(c: VisualClip, frameFit: Fit): Crop | undefined {
    if (!c.crop) return undefined;
    if ((c.fit ?? frameFit) !== 'COVER') return undefined;
    if (c.crop.w >= 1 - 1e-6 && c.crop.h >= 1 - 1e-6) return undefined;
    return cropToWire(c.crop);
}

/**
 * The boundary a new project opens on, and the fallback for a document that reached `toWire`
 * without one. `transition` is REQUIRED on the wire, so there is no honest way to emit
 * nothing; a restored bundle missing it gets the same boundary a new project opens on rather
 * than an `undefined` typed as a `Transition` (P5a.2 review, M3).
 *
 * NONE since round 4 (owner: "By default, transition should be None", A4). The 500 ms is kept
 * on the object so the cross-fade stays ONE click away: `transition-control` switches type with
 * `current.durationMs || default`, and `effectiveDurationMs`/`transitionToWire` both read a NONE
 * boundary as costing nothing whatever it claims, so the number is invisible until it is wanted.
 */
export const DEFAULT_TRANSITION: Transition & { durationMs: number } = { type: 'NONE', durationMs: 500 };

function transitionToWire(t: Transition): Transition {
    const durationMs = t.type === 'NONE' || !t.durationMs ? undefined : Math.round(t.durationMs);
    return compact({ type: t.type, durationMs }) as Transition;
}

/** A clip's `transitionIn`, which is genuinely optional — absent means "use the default". */
function optionalTransitionToWire(t: Transition | null | undefined): Transition | undefined {
    return t ? transitionToWire(t) : undefined;
}

function photoToWire(c: PhotoClip, index: number, frameFit: Fit): PhotoClip {
    return compact({
        input: c.input,
        kind: 'PHOTO' as const,
        holdMs: Math.round(c.holdMs),
        // `NONE` IS the schema default, so it is omitted like every other default; `SLOW_ZOOM` and
        // `PAN` always go on the wire. The client's default for a NEW photo is `NONE` too since QA
        // round 2 Q5 (`views/hold-defaults.ts`; S-30 had it `SLOW_ZOOM`), so a freshly placed
        // photo carries no `motion` on the wire -- and this projection never invents one for a
        // clip that has none.
        motion: !c.motion || c.motion === 'NONE' ? undefined : c.motion,
        fit: c.fit,
        rotate: c.rotate ? c.rotate : undefined,
        flipH: c.flipH ? true : undefined,
        flipV: c.flipV ? true : undefined,
        crop: cropOnWire(c, frameFit),
        // Clip 0 has no incoming boundary; the edge ignores a `transitionIn` there
        // (Composition.effectiveTransitionAt), and emitting one would move the submission
        // fingerprint for something that renders nothing.
        transitionIn: index === 0 ? undefined : optionalTransitionToWire(c.transitionIn),
    }) as PhotoClip;
}

/**
 * A video clip's two trim bounds as whole milliseconds the edge can accept — THE TRIM RULE.
 *
 * **Clamp to the source end, floor, and never below `trimStartMs + MIN_CLIP_MS`.** The clamp to
 * the source is not here, because this module has no probe: the owner does it on every commit
 * (`studio-tab.component.tsx`'s `withKnownDuration` / `onClipDuration`, both against
 * `Math.floor(durationMs)`), and this function is the last gate before the wire.
 *
 * **Floor, never round** (QA workflow 7b59d2a8, 2026-09-22). A trim bound is a bound on a REAL
 * FILE and `Math.round` can only ever move it PAST one: the failing export's source measured
 * 13 210.703125 ms, ironman holds that file as 13 210 ms (`ProbeInfo.durationMs` is an `Int` and
 * `ComposeVideoStrategy.probeInfoOf` truncates), the wire said 13 211, and `CompositionValidator`
 * refused the whole composition with `trim_out_of_range` — "Clip 1 is trimmed past the end of its
 * video." Every video whose duration is not a whole millisecond failed to export.
 *
 * **Why a millisecond off the end is safe, and where it would stop being safe.** ffmpeg's `-t`
 * keeps frames whose PTS is STRICTLY LESS than the cut, so a cut landing exactly on the last
 * frame's presentation time drops that frame. The floor costs at most 1 ms, and one frame is
 * 16.7 ms (60 fps) to 41.7 ms (24 fps) — two orders away — so the floored end stays after the last
 * frame's PTS for any source whose declared duration reaches the end of its last frame. Measured
 * on the owner's QA file: 397 frames at 30 fps, last PTS 13.200000 s, container end 13.233333 s,
 * probe 13 210.703125 ms ⇒ a floored 13 210 ms sits 10 ms after the last frame starts and keeps
 * it. A client cannot see frame PTSs, so this is the strongest rule statable here; the real fix
 * for the untrimmed case is to stop sending a number at all for a clip nobody trimmed, which is a
 * schema contract change and a follow-up, not this.
 *
 * **MIN_CLIP_MS comes off the START, never the end.** Flooring both bounds cannot break the
 * minimum on its own — `floor(end) - floor(start) > (end - start) - 1 >= MIN_CLIP_MS - 1`, and
 * both floors are whole milliseconds, so the floored span is never under MIN_CLIP_MS (the spec
 * asserts it across the fractional range). The guard is stated anyway, because the rule is
 * "never below `trimStartMs + MIN_CLIP_MS`" and a rule that is only true by arithmetic should be
 * readable in the code; it pulls the START down rather than pushing the END up, since raising the
 * end is exactly how a trim gets past the file.
 *
 * **A clip that was ALREADY shorter than the minimum is left exactly as it is.** `check.ts` runs
 * on the wire, and the edge's rules do too: an empty trim has to ARRIVE empty, or "Clip 1 has no
 * length. Drag its edge to make it longer." turns into 500 ms of video nobody asked for. This
 * projection reports the document; it does not repair it.
 */
function trimsToWire(
    c: VideoClip,
    fps: number,
    probe?: ProbeInfo | null,
): { trimStartMs: number; trimEndMs?: number } {
    const trimEndMs = Math.floor(c.trimEndMs);
    const flooredStart = Math.floor(c.trimStartMs);
    const trimStartMs = c.trimEndMs - c.trimStartMs >= MIN_CLIP_MS
        ? Math.min(flooredStart, Math.max(0, trimEndMs - MIN_CLIP_MS))
        : flooredStart;
    const omittable = endOmittable(probe) && endsAtSource(trimEndMs, fps, probe?.durationMs);
    return { trimStartMs, trimEndMs: omittable ? undefined : trimEndMs };
}

/**
 * **May this clip's end be left off the wire at all?** — the gate `endsAtSource` runs behind.
 *
 * Omission is a SENTENCE ABOUT THE RECIPIENT: "you work out where this file stops". It is only
 * true when the recipient can. The edge resolves an absent end by probing the source
 * (`Clip.effectiveTrimEndMs` → `ProbeInfo.durationMs`), and `ffprobe` answers `duration=N/A`
 * and `nb_frames=N/A` for an animated PNG — so for that class of source the resolution lands on
 * 0, the clip has no length, and the render is refused before a frame is drawn. That is export
 * failure `9vbjspipq2gw`, from the owner's own hand-test: a 1.4 s APNG the browser had already
 * measured, sent as `{"input":2,"kind":"VIDEO","trimStartMs":0}`, planned by the edge as 0 ms.
 *
 * So an animated image always carries its end, and the number is the browser's own — read from
 * the frame delays in the same bytes that answered "is this animated" (`media/animated.ts`), which
 * is a BETTER measurement than the probe, not a worse one: it is the container's own declared
 * timing rather than a stream duration inferred from it.
 *
 * This is not a schema change. `trimEndMs` stays optional, for the case it was made optional for
 * — a real video container, where the only end this client could name is a probed stream duration
 * that can stop 23 ms before the file does (QA 7b59d2a8). It is the one case where the recipient
 * knows the file's end better than the browser does, and it is exactly the case kept.
 *
 * **The second half of the rule lives in `build-request.ts`.** An animated source the browser
 * could NOT measure (a truncated read, a file over `ANIMATED_TIMING_BYTES`, avif/heic, fewer than
 * two frames — `sniffAnimated` answers `null` rather than a short sum) has no honest end to state
 * and no honest absence either: the export is REFUSED there rather than rounded off here. A
 * projection reports the document; it does not invent a length for it.
 */
function endOmittable(probe?: ProbeInfo | null): boolean {
    return probe?.animated !== true;
}

/**
 * Is this clip's end the SOURCE'S own end — i.e. did the user never move the end handle?
 *
 * True inside a window of ONE FRAME either side of the source's floored duration, and false
 * everywhere else, including when the source was never measured. Each half of the window earns
 * its place:
 *
 * **Above** — the document's end can sit a millisecond or two past the probe because the two
 * numbers come from different measurements of the same file (the QA file: the editor held
 * 13 211, the edge's `ProbeInfo.durationMs` held 13 210, from one measurement of
 * 13 210.703125). That millisecond is not a trim, and sending it is what `trim_out_of_range`
 * was refusing. **Bounded, not `>=`**: a trim four seconds past a five-second source is a real
 * mis-trim, and the browser must still SAY so rather than quietly turn it into "the whole file"
 * — `checkDocument` reads this same wire.
 *
 * **Below** — the last frame's presentation time lies within one frame of the end by definition,
 * and ffmpeg keeps frames whose PTS is strictly LESS than the cut. So a cut anywhere in this
 * window risks landing exactly ON the last frame's PTS and dropping it (on the QA file the
 * frame-grid quantisation does precisely that: `ms(frames(13210)) = 13 200`, the last frame's own
 * PTS). Reading to EOF cannot drop it and cannot cut its display short. The cost — a user who
 * trimmed exactly one frame off the end gets that frame back — is smaller than the defect, and
 * one frame at the very end is not a trim anyone can aim.
 */
function endsAtSource(trimEndMs: number, fps: number, sourceDurationMs?: number | null): boolean {
    if (sourceDurationMs == null || !(sourceDurationMs > 0)) return false;
    const oneFrameMs = 1000 / Math.max(1, fps);
    const sourceEndMs = Math.floor(sourceDurationMs);
    return trimEndMs >= sourceEndMs - oneFrameMs && trimEndMs <= sourceEndMs + oneFrameMs;
}

function videoToWire(
    c: VideoClip,
    index: number,
    frameFit: Fit,
    fps: number,
    probe?: ProbeInfo | null,
): VideoClip {
    const audio = c.audio ? clipAudioOf(c) : null;
    const audioNeeded = audio !== null && (audio.mode !== 'KEEP' || audio.volume !== 1);
    const trims = trimsToWire(c, fps, probe);
    return compact({
        input: c.input,
        kind: 'VIDEO' as const,
        // `trimStartMs` is `required` in the schema, so it is always written even at 0 -- unlike
        // every optional below. `trimEndMs` is NOT required any more: `trimsToWire` omits it for a
        // clip the user never trimmed at its end, and absent means "to the end of the source", so
        // the edge emits no `-t` and ffmpeg reads to EOF. That is the only correct shape, because
        // the only end this client could name is a probed STREAM duration and the QA file's stops
        // 23 ms before the file does. `compact()` drops the undefined.
        trimStartMs: trims.trimStartMs,
        trimEndMs: trims.trimEndMs,
        audio: audioNeeded
            ? (compact({ mode: audio.mode, volume: audio.volume === 1 ? undefined : audio.volume }) as ClipAudio)
            : undefined,
        fit: c.fit,
        rotate: c.rotate ? c.rotate : undefined,
        flipH: c.flipH ? true : undefined,
        flipV: c.flipV ? true : undefined,
        crop: cropOnWire(c, frameFit),
        transitionIn: index === 0 ? undefined : optionalTransitionToWire(c.transitionIn),
    }) as VideoClip;
}

function musicToWire(m: MusicClip): AudioClip {
    return compact({
        input: m.input,
        kind: 'AUDIO' as const,
        atMs: m.atMs ? Math.round(m.atMs) : undefined,
        // The song's trim bounds are floored for exactly the reason the video's are (see
        // `videoToWire`): the validator checks `trimEndMs` against the MP3's probed duration on
        // the same tier, and a rounded-up bound is "The music is trimmed past the end of its
        // file." `atMs` is a position on the TIMELINE, not a bound on a file, so it still rounds.
        trimStartMs: m.trimStartMs ? Math.floor(m.trimStartMs) : undefined,
        // The song ALWAYS carries its number, even though the schema now allows it to be omitted.
        // Omitting means "to the end of the file", and the editor's end is deliberately NOT the
        // file's end: a placed song is trimmed to the video (`followsVideo`), and a hand-trimmed
        // one is held `MUSIC_LENGTH_TOLERANCE_MS` back from the probe because MP3 lengths are
        // decoder-dependent (`check.ts` `musicTrimCeilingMs`). There is no untrimmed-song case in
        // this client to omit for. The server accepts an absent music trim for the clients that
        // do have one — the public API included.
        //
        // S2-02 (review 7): ...except the one case this client now HAS: a song that follows a video at least as long
        // as itself plays to its file's end (`toEnd`), and "to the end of the file" is exactly what an absent end
        // means -- no tolerance, no decoder disagreement, no 0.25 s lost.
        trimEndMs: m.toEnd ? undefined : Math.floor(m.trimEndMs),
        volume: (m.volume ?? 1) === 1 ? undefined : m.volume,
        fadeInMs: m.fadeInMs ? Math.round(m.fadeInMs) : undefined,
        fadeOutMs: m.fadeOutMs ? Math.round(m.fadeOutMs) : undefined,
        duck: m.duck ? true : undefined,
    }) as AudioClip;
}

/**
 * The editor's document as the exact shape the published schema accepts.
 *
 * An optional field is written only when its value is NOT the schema default, so the
 * submission carries what the user chose and nothing else. `required` fields are always
 * written, defaults included. The audio track is omitted entirely when there is no music
 * rather than sent as an empty track: both are legal (`AudioTrack.clips` has `minItems: 0`),
 * and the shorter one says the same thing.
 *
 * @param probes what the browser measured for each input, in the DOCUMENT'S input space (the same
 *   map `checkDocument` and `plan` take). It is used for one decision: whether a video clip's end
 *   is the source's own end AND the recipient could work that end out for itself (`endOmittable`),
 *   in which case `trimEndMs` is OMITTED and the edge reads to EOF. An animated image is never
 *   omittable — `ffprobe` cannot time one — so it always states its end.
 *   Optional, and omitting it is safe — every clip then carries its number, which is exactly the
 *   behaviour before this contract change. Pass it wherever the result is SENT or CHECKED
 *   (`build-request.ts`, `checkDocument`, `planOf`); the display-only callers need not.
 *
 *   Deliberately NOT passed by `exportSignature`: the signature is a change detector, and one that
 *   flipped with probe availability would bump the revision for a timeline nobody touched.
 */
export function toWire(doc: StudioDocument, probes?: ProbeMap | null): StudioCompositionV1 {
    const frameFit: Fit = doc.frame.fit;
    const clips = doc.clips.map((c, i) => (
        c.kind === 'PHOTO'
            ? photoToWire(c, i, frameFit)
            : videoToWire(c, i, frameFit, doc.frame.fps, probes?.[c.input])));
    const tracks: Track[] = [
        { kind: 'VISUAL', layout: 'SEQUENTIAL', clips: clips as VisualTrack['clips'] },
    ];
    if (doc.music) {
        tracks.push({ kind: 'AUDIO', layout: 'POSITIONED', clips: [musicToWire(doc.music)] });
    }
    return {
        version: COMPOSITION_VERSION,
        projectId: doc.projectId,
        revision: doc.revision,
        createdWith: CREATED_WITH,
        frame: {
            width: doc.frame.width,
            height: doc.frame.height,
            fps: doc.frame.fps,
            fit: doc.frame.fit,
            backgroundColor: doc.frame.backgroundColor,
        },
        transition: transitionToWire(doc.transition ?? DEFAULT_TRANSITION),
        tracks: tracks as StudioCompositionV1['tracks'],
    };
}

/**
 * One clip, structurally copied — no object is shared with the wire it came from.
 *
 * A spread is a SHALLOW copy, and `crop`, `audio` and `transitionIn` are objects (P5a.2
 * review, M2). Sharing them would make an in-place edit of a restored clip reach back into
 * the wire object, into the undo snapshot taken from the same wire, and into the other tab's
 * copy of a document rehydrated from one parse -- a class of bug that shows up as an undo
 * that does not undo. Written out rather than `structuredClone`d because the field set is
 * fixed by the schema, this is three lines, and `structuredClone` is absent in the older
 * Safari the frontend still supports.
 */
function clipFromWire(clip: VisualClip): VisualClip {
    const copy: VisualClip = { ...clip };
    if (clip.crop) copy.crop = { ...clip.crop };
    if (clip.transitionIn) copy.transitionIn = { ...clip.transitionIn };
    if (clip.kind === 'VIDEO' && clip.audio) (copy as VideoClip).audio = { ...clip.audio };
    return copy;
}

/**
 * The inverse, for re-edit and for restoring a persisted project.
 *
 * NOTHING IS DEFAULTED HERE beyond what the wire already says. In particular a photo clip without
 * `motion` comes back without `motion` -- which the preview and the inspector both read as `NONE`,
 * exactly as the server does. The client's own default for a new photo is `NONE` as well (QA round
 * 2 Q5; S-30 had it `SLOW_ZOOM`), but that is `hold-defaults.ts`'s decision, not this function's:
 * a saved project must render tomorrow as it did yesterday, whatever the default for new clips is.
 */
export function fromWire(c: StudioCompositionV1): StudioDocument {
    const music = musicClipOf(c);
    return {
        projectId: c.projectId,
        revision: c.revision,
        frame: { ...c.frame },
        transition: { type: c.transition.type, durationMs: effectiveDurationMs(c.transition) },
        clips: visualClipsOf(c).map(clipFromWire),
        // `AudioClip` is scalars only, so a spread already separates it from the wire.
        music: music ? { ...music } : null,
    };
}

/** A document with a frame and no clips — what a freshly opened Studio holds. */
export function emptyDocument(projectId: string, frame: Frame): StudioDocument {
    return {
        projectId,
        revision: 1,
        frame: { ...frame },
        transition: { ...DEFAULT_TRANSITION },
        clips: [],
        music: null,
    };
}

// --------------------------------------------------------------------- project identity

/** The schema's `projectId` shape, and `IdempotentScheduleKey.PROJECT_ID_PATTERN`. */
export const PROJECT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const PROJECT_ID_LENGTH = 12;

/**
 * Lower case and digits only, with `l`, `o`, `0` and `1` left out so a project id read
 * aloud or retyped from a URL is unambiguous.
 *
 * The absence of upper case is also a SAFETY property, not a style one: persistence stores
 * projects at `STUDIO-<projectId>` and its own bookkeeping at `STUDIO-INDEX` and
 * `STUDIO-LATEST-<pageSlug>` (STUDIO_DESIGN.md 6.2). `projectId` is
 * `[A-Za-z0-9_-]{1,64}`, which admits both of those literals -- so a minted id that cannot
 * contain an upper-case letter cannot collide with either. `isReservedProjectId` covers an
 * id that arrives from somewhere other than this function.
 */
const PROJECT_ID_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';

export function newProjectId(): string {
    const out: string[] = [];
    const n = PROJECT_ID_ALPHABET.length;
    const bytes = randomBytes(PROJECT_ID_LENGTH);
    if (bytes) {
        for (let i = 0; i < PROJECT_ID_LENGTH; i += 1) out.push(PROJECT_ID_ALPHABET[bytes[i] % n]);
    } else {
        for (let i = 0; i < PROJECT_ID_LENGTH; i += 1) {
            out.push(PROJECT_ID_ALPHABET[Math.floor(Math.random() * n)]);
        }
    }
    return out.join('');
}

/**
 * `null` rather than a throw when there is no usable CSPRNG, so `newProjectId` can fall back.
 *
 * The try/catch is not defensive padding: `crypto.getRandomValues` rejects a typed array
 * from a different realm with a TypeError, which is exactly the shape of a jsdom test
 * environment carrying Node's WebCrypto, and a project id is not a secret -- losing
 * entropy quality is a far smaller failure than being unable to open the editor at all.
 */
function randomBytes(length: number): Uint8Array | null {
    const source = typeof globalThis === 'undefined' ? undefined : globalThis.crypto;
    if (!source || typeof source.getRandomValues !== 'function') return null;
    try {
        return source.getRandomValues(new Uint8Array(length));
    } catch {
        return null;
    }
}

export function isValidProjectId(id: string | null | undefined): boolean {
    return typeof id === 'string' && PROJECT_ID_PATTERN.test(id) && !isReservedProjectId(id);
}

/** Ids that would occupy persistence's own keys. See `newProjectId`'s note. */
export function isReservedProjectId(id: string): boolean {
    return id === 'INDEX' || id.startsWith('LATEST-');
}

// --------------------------------------------------------------------- the revision rule

/** What was submitted last, as persistence remembers it. */
export interface ExportSubmission {
    signature: string;
    revision: number;
}

/**
 * A stable string for everything that changes the FILE the server would produce.
 *
 * Three inputs, because §3.3 says all three matter and two of them are easy to forget:
 *  - the composition, minus its own revision (the revision is the OUTPUT of this
 *    comparison, so feeding it back in would make every project permanently "changed");
 *  - the uploaded input ids, in order -- `originalInputFiles` is deliberately in neither
 *    the idempotency key nor the server's fingerprint (STUDIO_DESIGN.md:364-370), which
 *    makes re-uploading the same project a REPLAY unless the editor bumps `revision`;
 *  - the export options, which on this page is the Options V2 compression group the export
 *    sheet mounts (P5b). The server's fingerprint hashes the options blob, so a quality
 *    preset changed without touching the timeline is an honest 409 unless we bump.
 *
 * `canonicalJson`, not `JSON.stringify`, for the reason canonical.ts states at length.
 */
export function exportSignature(
    doc: StudioDocument,
    inputs: readonly string[],
    options: Readonly<Record<string, unknown>>,
): string {
    return canonicalJson({
        composition: toWire({ ...doc, revision: 1 }),
        inputs: [...inputs],
        options,
    });
}

/**
 * The revision to submit: the same one when nothing export-affecting changed (so the server
 * replays the first render), the next one when something did.
 *
 * This is the single bump path the Phase 3 carry-forward fixes ("the editor bumps
 * `revision` on ANY export-affecting change, inputs and the compression group included",
 * STUDIO_PLAN_P3/progress.md:35). Every mutating operation elsewhere in the editor changes
 * the DOCUMENT; none of them touches `revision` by hand. The bump happens here, once, at
 * submission time, from a comparison of `exportSignature` values -- which is why a mutation
 * that forgets to bump cannot exist, and why a re-render that changes nothing cannot bump.
 *
 * Capped at `MAX_REVISION` because `IdempotentScheduleKey.forProjectRevision` REQUIRES
 * `revision in 1..1_000_000` and throws otherwise -- a client that ran past it would turn
 * an export into a 500. Reaching 1e6 exports of one project is not a real scenario; being
 * unable to explain the 500 if it happened is.
 */
export function nextRevision(
    doc: StudioDocument,
    signature: string,
    /**
     * What was submitted last. `null` means "never submitted", and it must mean ONLY that:
     * after a reload it comes from `state/persistence.ts`'s `storedSubmission(record)`, which
     * round-trips it with the document for this call (whole-branch review, I1). A component
     * field that started empty on every mount would make the second export of a restored
     * project re-send `(projectId, doc.revision)` under a changed fingerprint — the 409.
     */
    last: ExportSubmission | null,
): number {
    if (!last) return doc.revision;
    if (last.signature === signature) return last.revision;
    return Math.min(MAX_REVISION, Math.max(doc.revision, last.revision) + 1);
}

/**
 * The document at the revision it would be submitted at — the one `touch()`/`bumpRevision()`
 * path the Phase 3 carry-forward asks for, expressed so there is nothing else to call.
 *
 * P5b's export sheet calls this, sends `toWire(result.document)` and stores
 * `result.submission` for the next comparison. It never assigns `revision` itself.
 *
 * `last` IS THE RECORD'S, NOT THE COMPONENT'S (review I1). On a restore it is
 * `storedSubmission(readProject(sid))`; on a save the caller writes `built.submission` back
 * onto the record. Holding it only in a component instance is what makes export → edit →
 * reload → export a 409, and that is the one flow persistence exists for.
 */
export function touchForExport(
    doc: StudioDocument,
    inputs: readonly string[],
    options: Readonly<Record<string, unknown>>,
    last: ExportSubmission | null,
): { document: StudioDocument; submission: ExportSubmission } {
    const signature = exportSignature(doc, inputs, options);
    const revision = nextRevision(doc, signature, last);
    return {
        document: revision === doc.revision ? doc : { ...doc, revision },
        submission: { signature, revision },
    };
}
